import {Select, Table, TableProps, Input} from "antd";
import {useState} from "react";
import {convertToCSV, downloadCSV} from "../../utils/utils";
import {chemicalElements, ELEMENTS} from "../../utils/consts";
import {SortOrder} from "antd/es/table/interface";

interface DataType {
		key: React.Key;
		name: string;
		chinese: number;
		math: number;
		english: number;
}

export interface IDataTable {
	data: any[];
	fetchData: (where?: string) => Promise<void>;
	alias: string;
	dataColumns: {label: string, value: string}[]
}
export const DataTable = ({data, fetchData, alias, dataColumns}: IDataTable) => {

	const [conditions, setConditions] = useState<{ element: string, operator: string, num: string }[]>([]);
	const [element, setElement] = useState<string>();
	const [operator, setOperator] = useState<string | null>(null);
	const [num, setNum] = useState<string | undefined>();
	const [logic, setLogic] = useState<string | null>('AND');

	const columns = dataColumns.map((column, idx) => {
		const isId = column.value === 'id';
		const isText = ['_c', '_r', '_ft', '_sn'].includes(column.value);
		const width = isId ? 80 : isText ? 130 : 80;
		return {
			title: column.label,
			dataIndex: column.value,
			width,
			sorter: (a: any, b: any) => {
				const valA = a[column.value];
				const valB = b[column.value];
				if (!valA && !valB) return 0;
				if (!valA) return -1;
				if (!valB) return 1;
				const numA = parseFloat(valA);
				const numB = parseFloat(valB);
				if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
				return String(valA).localeCompare(String(valB));
			},
			sortDirections: ["ascend", "descend"] as SortOrder[],
			...(idx < 2 ? { fixed: 'left' as const } : {}),
			ellipsis: true,
		};
	});

	const handleAddCondition = () => {
		setConditions([...(conditions || []), { element: element!, operator: operator!, num: num?.replace(',','.')! }]);
	}

	const handleRemoveCondition = (index: number) => {
		const updated = [...conditions];
		updated.splice(index, 1);
		setConditions(updated);
	}

	const handleSubmit = async () => {
		const where = conditions.map((c) =>
			`(c.chemicalElementId = ${chemicalElements[c.element as keyof typeof chemicalElements]} and c.concentrationLevel ${c.operator} ${c.num})`).join(` ${logic} `);
		await fetchData(where);
	}

	const handleExport = () => {
		const csvData = convertToCSV(data);
		if (!csvData) { alert('No data available for export!'); return; }
		downloadCSV(csvData, 'exported_data');
	};

	const selectValues = dataColumns.map((key) => ({value: key.value, label: key.label}));
	const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra) => {
		console.log('params', pagination, filters, sorter, extra);
	};

	return (
		<div style={{width: "100%"}}>
			{/* Existing condition chips */}
			{conditions.length > 0 && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
					{conditions.map((condition, index) => (
						<div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink)' }}>
							<span style={{ background: 'var(--panel)', padding: '2px 8px', borderRadius: 2, fontWeight: 600 }}>
								{condition.element} {condition.operator} {condition.num}
							</span>
							<button className="data-btn" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => handleRemoveCondition(index)}>✕</button>
						</div>
					))}
				</div>
			)}

			{/* Single-row compact filter bar */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', marginBottom: 8, flexWrap: 'wrap', background: 'var(--panel)', padding: '8px 12px', border: '1px solid var(--border)' }}>
				<Select
					showSearch size="small"
					value={element} options={selectValues}
					style={{ width: 130 }} allowClear
					onClear={() => setElement(undefined)}
					placeholder="Column"
					onChange={(value) => setElement(value)}
					filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
				/>
				<Select
					size="small" value={operator}
					options={[{value:'=',label:'='},{value:'>',label:'>'},{value:'<',label:'<'},{value:'>=',label:'>='},{value:'<=',label:'<='}]}
					style={{ width: 70 }} allowClear
					onClear={() => setOperator(null)}
					onChange={(value) => setOperator(value)}
					placeholder="Op"
				/>
				<Input
					size="small" value={num} style={{ width: 100 }}
					onChange={(e) => setNum(e.target.value)}
					placeholder="Value"
				/>
				<button className="data-btn" disabled={!(element && operator && num)} onClick={() => handleAddCondition()} style={{ padding: '4px 10px', fontSize: 12 }}>
					+ Add
				</button>
				<Select
					size="small"
					options={[{value:'AND',label:'AND'},{value:'OR',label:'OR'}]}
					style={{ width: 70 }}
					defaultValue={logic}
					onChange={(value) => setLogic(value)}
				/>

				{element && (
					<span style={{ fontSize: 11, color: 'var(--muted)', marginLeft: 4 }}>
						{element}: {ELEMENTS.find((i) => i.label === element)?.minimumValue} – {ELEMENTS.find((i) => i.label === element)?.maximumValue}
					</span>
				)}

				<div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
					<button className="data-btn" onClick={handleExport} style={{ padding: '4px 12px', fontSize: 12 }}>Export</button>
					<button className="data-btn" onClick={handleSubmit} style={{ padding: '4px 12px', fontSize: 12 }}>Search</button>
				</div>
			</div>

			{/* Table */}
			<div style={{ overflow: "scroll", marginBottom: 40 }}>
				<Table
					className="gf-table"
					pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['20','50','100'] }}
					scroll={{ x: columns.reduce((sum, c) => sum + (c.width as number), 0), y: '65vh' }}
					sticky
					style={{ width: "100%" }}
					columns={columns}
					dataSource={data}
					onChange={onChange}
				/>
			</div>
		</div>
	)
}
